import { useEffect, useMemo, useState } from 'react';
import Items, { defaultConfigComponent as ItemConfigComponent } from '../Items/Items';
import actStore from '@flux/actions/actStore';
import ItemColumns from './ItemColumns';
import { useAppDispatch } from '@redux/store';
import {
  IDataGridProps,
  IAction,
  IDataGridConfig,
  TInheritedProps,
  generateExpanableColumn,
  generateCellFromStatusIndicator,
  ICollapsableProps,
  TTextType,
} from '@ts/storybook';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { fetchAllStatusIndicators } from '@services/ApiService';
import { IStatusIndicator, IConfigComponent, CElement, EFlavors, TFlavor } from '@ts/ui-models';
import { useRouterContext } from '@utils/context/RouterContext';
import WrapperDataGrid from '@modules/dataGrid/WrapperDataGrid';

export interface ItemBaseProps<Multiple extends boolean> {
  configComponent: IConfigComponent;
  flavorId?: TFlavor;
  title?: TTextType;
  inheritedProps?: TInheritedProps<Multiple>;
  nameColumns: string[]; //For custom columns from parent added
  row?: any; //From collapsable
  propsDataGrid?: IDataGridProps<false>['propsDataGrid'];
}

const ItemsBase = <Multiple extends boolean>(props: ItemBaseProps<Multiple>) => {
  const dispatch = useAppDispatch();
  const { navigate, transferPropsRoute } = useRouterContext();
  const filterID = transferPropsRoute?.query?.filterID;

  const [config, setConfig] = useState<IDataGridConfig>({});
  const [columns, setColumns] = useState<GridColDef<any>[]>([]);

  //#region Functions
  const getAllStatusIndicators = async () => {
    const data = await fetchAllStatusIndicators(dispatch);
    return data.rows;
  };

  const getColumnsFromStatusIndicators = (status: IStatusIndicator[]): GridColDef<any>[] => {
    const columns: GridColDef<any>[] = [];
    status?.forEach(statu => {
      columns.push(generateCellFromStatusIndicator(statu));
    });
    return columns;
  };

  const actionsByFlavors = (): IAction[] => {
    const actionByFlavor: IAction[] = [];

    //See on map
    if (
      ['allItem', 'asset', 'device', 'livestock', 'person', 'process', 'vehicle', 'wipPart', 'itemInCustody'].includes(
        props.flavorId ?? 'allItem'
      )
    ) {
      actionByFlavor.push({
        id: 'openModalMap',
        label: 'View on map',
        icon: 'my_location',
        //action: 'openModalMap',
        fDisabled: (props, row) => {
          const tElement = new CElement(row);
          return tElement.haveFlavor(EFlavors.LOCATION);
        },
        onClick: handleClick => actStore.setActionTableV3({ action: 'openModalMap', row: handleClick() }),
      });
    }

    return actionByFlavor;
  };

  const actions = (): IAction[] => {
    const globalActions: IAction[] = [
      {
        id: 'openModalReading',
        label: 'Readings',
        icon: 'manage_search',
        onClick: handleClick => actStore.setActionTableV3({ action: 'openModalReading', row: handleClick() }),
      },
      {
        id: 'openModalEvent',
        label: 'Events',
        icon: 'event_note',
        onClick: handleClick => actStore.setActionTableV3({ action: 'openModalEvent', row: handleClick() }),
      },
    ];

    return globalActions.concat(actionsByFlavors());
  };

  const getColumns = useMemo((): GridColDef<any>[] => {
    const { nameColumns } = props;

    const columnsProps = {
      configComponent: props.configComponent,
      inheritedProps: props.inheritedProps,
      flavorId: props.flavorId,
      navigate: navigate,
    };

    const itemColumns = ItemColumns(columnsProps);
    const columnsBaseAllItemAndFlavor = [
      itemColumns.id,
      itemColumns.name,
      itemColumns.flavors,
      itemColumns.elementType,
      itemColumns.status,
      itemColumns.statusAlive,
      itemColumns.labels,
      itemColumns.attachedDevices,
      itemColumns.createdAt,
      itemColumns.lastActivity,
      itemColumns.parents,
      itemColumns.children,
      itemColumns.activeNotifications,
      itemColumns.openTasks,
    ];
    let columns: GridColDef<any>[] = [];
    if (nameColumns) {
      columns = columns.concat(columnsBaseAllItemAndFlavor).concat(nameColumns.map(key => itemColumns[key]));
    }

    return columns;
  }, [props.inheritedProps, props.nameColumns, props.flavorId]);
  const shouldShowExpanableCell = (params: GridRenderCellParams) => params.row?.nHierarchyChilds;
  //#endregion

  useEffect(() => {
    const buildColumns = async () => {
      const data = await getAllStatusIndicators();
      const statusColumns = getColumnsFromStatusIndicators(data);
      setColumns([...(props.row ? [] : [generateExpanableColumn(shouldShowExpanableCell)]), ...getColumns, ...statusColumns]);
      setConfig({ columnVisibilityModel: statusColumns.map(c => c.field).reduce((a, c) => ({ ...a, [c]: false }), {}) });
    };

    buildColumns();
  }, []);

  if (Object.keys(config).length === 0) return null;

  return (
    <WrapperDataGrid
      key={filterID ?? 'itemBase'}
      title={props.title}
      configComponent={props.configComponent}
      dataGridConfig={config}
      columns={columns}
      extraActions={actions()}
      dataGridMode={'server'}
      enabledFilters
      collapsableComponent={
        !props.row ? (clProps: ICollapsableProps<false>) => <Items {...clProps} configComponent={ItemConfigComponent} /> : null
      }
      //From granpa
      inheritedProps={props.inheritedProps} //EntitySelector
      propsDataGrid={props.propsDataGrid}
    />
  );
};

export default ItemsBase;
